/**
 * operatorProvisioningOutput — PURE client-side generators that turn a
 * provisioning draft (non-secret `data`) + transient `secrets` (held only in
 * React state, never persisted) into the deployment blueprint artifacts:
 *   - backend .env.production
 *   - frontend .env
 *   - DNS records checklist
 *   - Twilio webhook checklist
 *   - Resend verification checklist
 *   - first-admin setup checklist
 *   - deployment readiness summary
 *
 * No network, no side effects. Secrets are woven in here in-browser only.
 * Empty secrets render an explicit <SET_…> placeholder so nothing silent ships.
 */

export const slugify = (s) =>
  (s || "operator")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || "operator";

const v = (x, fallback = "") => (x === undefined || x === null || x === "" ? fallback : String(x));
const secret = (x, name) => (x ? String(x) : `<SET_${name}>`);
const ph = (label) => `<SET_${label}>`;
const bool = (x) => (x ? "true" : "false");

// ── Backend .env.production ───────────────────────────────────────────────
export function generateBackendEnv(data = {}, secrets = {}) {
  const dbName = `${slugify(data.company_name)}_dispatch`;
  const domain = v(data.deployment_domain, ph("DEPLOYMENT_DOMAIN"));
  const fromName = v(data.email_from_name, v(data.company_name, "Dispatch"));
  const fromEmail = v(data.resend_from_email, ph("RESEND_FROM_EMAIL"));
  return [
    "# ─────────────────────────────────────────────────────────────",
    `# ${v(data.company_name, "Operator")} — backend/.env (PRODUCTION)`,
    "# Generated by the Operator Provisioning Wizard. Fill every <SET_…>",
    "# value before deploying. NEVER commit this file to source control.",
    "# ─────────────────────────────────────────────────────────────",
    "APP_ENV=production",
    "ENVIRONMENT=production",
    "",
    "# Database (provision a dedicated MongoDB cluster for this operator)",
    `MONGO_URL=${ph("MONGO_URL")}`,
    `DB_NAME=${dbName}`,
    `EXPECTED_DB_NAME=${dbName}`,
    "",
    "# Security",
    `JWT_SECRET=${ph("JWT_SECRET_64_CHAR_RANDOM")}`,
    `SUPER_ADMIN_EMAILS=${v(data.admin_email)}`,
    "",
    "# Public URLs / CORS",
    `FRONTEND_URL=https://${domain}`,
    `CORS_ORIGINS=https://${domain}`,
    "",
    "# Email (Resend)",
    `RESEND_API_KEY=${secret(secrets.resend_api_key, "RESEND_API_KEY")}`,
    `EMAIL_FROM=${fromName} <${fromEmail}>`,
    "",
    "# Twilio (SMS / voice masking / inbound booking)",
    `TWILIO_ACCOUNT_SID=${v(data.twilio_account_sid, ph("TWILIO_ACCOUNT_SID"))}`,
    `TWILIO_AUTH_TOKEN=${secret(secrets.twilio_auth_token, "TWILIO_AUTH_TOKEN")}`,
    `TWILIO_SMS_ENABLED=${bool(data.twilio_sms_enabled)}`,
    `TWILIO_SMS_NUMBER=${v(data.twilio_sms_number)}`,
    `TWILIO_VOICE_MASK_NUMBER=${v(data.twilio_voice_mask_number)}`,
    `TWILIO_PUBLIC_BOOKING_NUMBER=${v(data.twilio_public_booking_number)}`,
    `TWILIO_DISPATCHER_NUMBER=${v(data.twilio_dispatcher_number)}`,
    `TWILIO_WHATSAPP_ENABLED=${bool(data.twilio_whatsapp_enabled)}`,
    `TWILIO_WHATSAPP_FROM=${v(data.twilio_whatsapp_from)}`,
    "",
    "# Payments (Stripe)",
    `STRIPE_API_KEY=${secret(secrets.stripe_secret_key, "STRIPE_SECRET_KEY")}`,
    `STRIPE_PUBLISHABLE_KEY=${v(data.stripe_publishable_key, ph("STRIPE_PUBLISHABLE_KEY"))}`,
    `STRIPE_WEBHOOK_SECRET=${secret(secrets.stripe_webhook_secret, "STRIPE_WEBHOOK_SECRET")}`,
    "",
    "# Maps",
    `GOOGLE_MAPS_API_KEY=${ph("GOOGLE_MAPS_API_KEY")}`,
    `GOOGLE_DIRECTIONS_API_KEY=${ph("GOOGLE_DIRECTIONS_API_KEY")}`,
    "",
    "# Observability (optional)",
    `SENTRY_DSN=${ph("SENTRY_DSN_OPTIONAL")}`,
    "",
    "# Launch tooling — keep OFF in production",
    "ALLOW_LAUNCH_RESET=false",
    "",
  ].join("\n");
}

// ── Frontend .env ─────────────────────────────────────────────────────────
export function generateFrontendEnv(data = {}) {
  const domain = v(data.deployment_domain, ph("DEPLOYMENT_DOMAIN"));
  return [
    `# ${v(data.company_name, "Operator")} — frontend/.env (PRODUCTION)`,
    `REACT_APP_BACKEND_URL=https://${domain}`,
    `REACT_APP_GOOGLE_MAPS_API_KEY=${ph("GOOGLE_MAPS_API_KEY")}`,
    `REACT_APP_GOOGLE_MAPS_MAP_ID=${ph("GOOGLE_MAPS_MAP_ID")}`,
    `REACT_APP_SENTRY_DSN=${ph("SENTRY_DSN_OPTIONAL")}`,
    "REACT_APP_SENTRY_ENVIRONMENT=production",
    `REACT_APP_TURNSTILE_SITE_KEY=${ph("TURNSTILE_SITE_KEY")}`,
    "REACT_APP_SHOW_DEMO_LOGINS=false",
    "REACT_APP_SHOW_LAUNCH_TOOLS=false",
    "",
  ].join("\n");
}

// ── Stripe / payments checklist ───────────────────────────────────────────
export function generateStripeChecklist(data = {}) {
  const domain = v(data.deployment_domain, "<deployment-domain>");
  const mode = v(data.stripe_mode, "test");
  return [
    `## Stripe / payments — ${v(data.company_name, "Operator")}`,
    "",
    "Only required if the operator wants to take card payments. Cash/account work without Stripe.",
    "",
    `- [ ] Create a Stripe account (or use the operator's existing one).`,
    `- [ ] Current mode: ${mode} — switch to LIVE keys before go-live.`,
    `- [ ] Publishable key (pk_...) → STRIPE_PUBLISHABLE_KEY ${data.stripe_publishable_key ? "(provided)" : "(still required)"}`,
    `- [ ] Secret key (sk_...) → STRIPE_API_KEY (never commit; paste at deploy time)`,
    `- [ ] Add a webhook endpoint in Stripe → Developers → Webhooks: ${`https://${domain}`}/api/webhook/stripe`,
    `- [ ] Copy the webhook signing secret (whsec_...) → STRIPE_WEBHOOK_SECRET`,
    `- [ ] Run a TEST card payment (4242 4242 4242 4242, any future expiry/CVC) and confirm it appears in Stripe and the ride is marked paid.`,
    `- [ ] Run a TEST refund and confirm it processes end-to-end.`,
    "",
  ].join("\n");
}

// ── Go-live test plan ─────────────────────────────────────────────────────
export function generateGoLiveTestPlan(data = {}) {
  const stripe = !!(data.stripe_publishable_key || data.payments_enabled);
  return [
    `## Go-live test plan — ${v(data.company_name, "Operator")}`,
    "",
    "Run through this on the live deployment before announcing the service.",
    "",
    "### Accounts & access",
    "- [ ] Admin login works",
    "- [ ] Dispatcher login works",
    "- [ ] Driver can log in",
    "- [ ] Driver can go online",
    "",
    "### Core dispatch journey",
    "- [ ] Booking can be created",
    "- [ ] Driver can accept the ride",
    "- [ ] Customer booking SMS sends",
    "- [ ] 'Driver arriving' SMS sends",
    "- [ ] Driver ↔ customer voice masking works",
    "- [ ] Public booking line rings the dispatcher",
    "",
    "### Email",
    "- [ ] Password reset email sends",
    "- [ ] Booking confirmation email sends",
    "",
    ...(stripe ? [
      "### Payments (card enabled)",
      "- [ ] Test card payment works",
      "- [ ] Refund test works",
      "",
    ] : [
      "### Payments",
      "- [ ] (Card payments not enabled — cash/account only)",
      "",
    ]),
    "### Operational dashboards",
    "- [ ] Revenue dashboard updates after a completed ride",
    "- [ ] Ride Health shows no stuck rides",
    "",
  ].join("\n");
}

// ── Grouped readiness checklist (UI) ──────────────────────────────────────
export const READINESS_GROUPS = [
  { key: "company", title: "Company basics", items: [
    { k: "company_name", label: "Company name provided" },
    { k: "trading_name", label: "Trading name provided (if different)", optional: true },
    { k: "licence_number", label: "Operator licence number provided" },
    { k: "service_area", label: "Operating area provided", optional: true },
    { k: "company_email", label: "Contact email provided" },
    { k: "twilio_dispatcher_number", label: "Dispatcher phone number provided", optional: true },
    { k: "twilio_public_booking_number", label: "Public booking number provided" },
  ]},
  { key: "domain", title: "Domain / branding", items: [
    { k: "deployment_domain", label: "Dispatch subdomain chosen" },
    { k: "brand_logo_url", label: "Logo URL provided", optional: true },
    { k: "brand_primary_color", label: "Brand colour selected", optional: true },
    { k: "support_email", label: "Support email chosen" },
  ]},
  { key: "twilio", title: "Twilio", items: [
    { k: "twilio_account_sid", label: "Account SID available" },
    { k: "twilio_auth_token", label: "Auth token available", secret: true },
    { k: "twilio_public_booking_number", label: "Public booking number added" },
    { k: "twilio_voice_mask_number", label: "Voice mask number set" },
    { k: "twilio_sms_number", label: "SMS number set", optional: true },
  ]},
  { key: "email", title: "Resend / email", items: [
    { k: "resend_api_key", label: "Resend API key available", secret: true },
    { k: "resend_domain", label: "Sending domain chosen" },
    { k: "resend_from_email", label: "From email selected" },
  ]},
  { key: "stripe", title: "Stripe / payments (only if card payments)", items: [
    { k: "stripe_publishable_key", label: "Publishable key available", optional: true },
    { k: "stripe_secret_key", label: "Secret key available", secret: true, optional: true },
    { k: "stripe_webhook_secret", label: "Webhook secret available", secret: true, optional: true },
  ]},
  { key: "admin", title: "First admin", items: [
    { k: "admin_name", label: "Owner/admin name provided" },
    { k: "admin_email", label: "Owner/admin email provided" },
    { k: "dispatcher_email", label: "Dispatcher email provided", optional: true },
  ]},
];

const filled = (data, secrets, item) =>
  item.secret
    ? !!(secrets && secrets[item.k])
    : data[item.k] !== undefined && data[item.k] !== null && data[item.k] !== "";

export function evaluateGroups(data = {}, secrets = {}) {
  let reqDone = 0, reqTotal = 0;
  const groups = READINESS_GROUPS.map((g) => {
    const items = g.items.map((it) => {
      const ok = filled(data, secrets, it);
      if (!it.optional) { reqTotal += 1; if (ok) reqDone += 1; }
      return { label: it.label, ok, optional: !!it.optional, secret: !!it.secret };
    });
    const reqItems = items.filter((i) => !i.optional);
    return {
      key: g.key, title: g.title, items,
      done: reqItems.filter((i) => i.ok).length, total: reqItems.length,
    };
  });
  return { groups, reqDone, reqTotal, percent: reqTotal ? Math.round((reqDone / reqTotal) * 100) : 0 };
}

// ── DNS records checklist ─────────────────────────────────────────────────
export function generateDnsChecklist(data = {}) {
  const domain = v(data.deployment_domain, "<deployment-domain>");
  const mail = v(data.resend_domain, "<resend-email-domain>");
  return [
    `## DNS records — ${v(data.company_name, "Operator")}`,
    "",
    `### App domain · ${domain}`,
    `- [ ] Point ${domain} at the hosting platform (A record or CNAME per host).`,
    `- [ ] Confirm HTTPS / TLS certificate is issued and valid for ${domain}.`,
    "",
    `### Email sending domain · ${mail} (Resend)`,
    "Copy the EXACT records from the Resend dashboard (Domains → Add domain):",
    `- [ ] MX record for ${mail} → feedback-smtp.<region>.amazonses.com (priority 10)`,
    `- [ ] SPF TXT on ${mail} → "v=spf1 include:amazonses.com ~all"`,
    `- [ ] DKIM CNAME #1 (resend._domainkey...) as shown in Resend`,
    `- [ ] DKIM CNAME #2 as shown in Resend`,
    `- [ ] DKIM CNAME #3 as shown in Resend`,
    `- [ ] DMARC TXT on _dmarc.${mail} → "v=DMARC1; p=none;"`,
    `- [ ] Click "Verify" in Resend and wait for all records to go green.`,
    "",
  ].join("\n");
}

// ── Twilio webhook checklist ──────────────────────────────────────────────
export function generateTwilioWebhookChecklist(data = {}) {
  const domain = v(data.deployment_domain, "<deployment-domain>");
  const base = `https://${domain}/api`;
  return [
    `## Twilio webhooks — ${v(data.company_name, "Operator")}`,
    "",
    `Configure in the Twilio Console for each number on account ${v(data.twilio_account_sid, "<account-sid>")}:`,
    "",
    `### Voice masking number · ${v(data.twilio_voice_mask_number, "<voice-mask-number>")}`,
    `- [ ] Voice → "A call comes in" (HTTP POST): ${base}/webhook/twilio/voice`,
    "",
    `### SMS number · ${v(data.twilio_sms_number, "<sms-number>")}`,
    `- [ ] Messaging → "A message comes in" (HTTP POST): ${base}/webhook/twilio/sms`,
    "",
    `### Public booking line · ${v(data.twilio_public_booking_number, "<booking-number>")}`,
    `- [ ] Voice → "A call comes in" (HTTP POST): ${base}/webhook/twilio/booking`,
    `- [ ] Voice → "Call status changes" (HTTP POST): ${base}/webhook/twilio/booking/status`,
    "",
    ...(data.twilio_whatsapp_enabled
      ? [
          `### WhatsApp sender · ${v(data.twilio_whatsapp_from, "<whatsapp-from>")}`,
          `- [ ] WhatsApp Sandbox/Sender inbound webhook (HTTP POST): ${base}/webhook/twilio/whatsapp`,
          "",
        ]
      : []),
    "### Payments",
    `- [ ] Stripe webhook endpoint (Dashboard → Developers → Webhooks): ${base}/webhook/stripe`,
    `- [ ] Copy the signing secret into STRIPE_WEBHOOK_SECRET.`,
    "",
  ].join("\n");
}

// ── Resend verification checklist ─────────────────────────────────────────
export function generateResendChecklist(data = {}) {
  const mail = v(data.resend_domain, "<resend-email-domain>");
  return [
    `## Resend email setup — ${v(data.company_name, "Operator")}`,
    "",
    `- [ ] Create a Resend account / workspace for this operator.`,
    `- [ ] Add sending domain: ${mail}`,
    `- [ ] Add the DNS records from the DNS checklist and verify (all green).`,
    `- [ ] Generate an API key (Sending access) → set RESEND_API_KEY.`,
    `- [ ] Confirm EMAIL_FROM uses a verified address: ${v(data.resend_from_email, "<from-email>")}`,
    `- [ ] Send a test email (password reset) and confirm delivery + Reply-To = ${v(data.support_email, "<support-email>")}.`,
    "",
  ].join("\n");
}

// ── First-admin setup checklist ───────────────────────────────────────────
export function generateFirstAdminChecklist(data = {}, secrets = {}) {
  const pwNote = secrets.admin_initial_password
    ? "(an initial password was provided in the wizard — share it securely, out of band)"
    : "(generate a strong initial password and share it securely)";
  return [
    `## First admin setup — ${v(data.company_name, "Operator")}`,
    "",
    `- [ ] After first deploy, seed the owner/admin account: ${v(data.admin_name, "<admin-name>")} <${v(data.admin_email, "<admin-email>")}> ${pwNote}.`,
    `- [ ] Log in as admin and immediately change the password (Setup → Security).`,
    `- [ ] Create the dispatcher account: ${v(data.dispatcher_name, "<dispatcher-name>")} <${v(data.dispatcher_email, "<dispatcher-email>")}>.`,
    `- [ ] Complete the in-app "Setup" wizard (company profile, licensing, base location, fares, branding).`,
    `- [ ] Verify the public landing page shows the operator branding + licence number.`,
    `- [ ] Add the first driver and walk one end-to-end test booking before going live.`,
    "",
  ].join("\n");
}

// ── Readiness evaluation ──────────────────────────────────────────────────
const READINESS_RULES = [
  { key: "deployment_domain", label: "Deployment domain set" },
  { key: "company_name", label: "Company name set" },
  { key: "company_email", label: "Company contact email set" },
  { key: "licence_number", label: "Operator licence number captured" },
  { key: "admin_email", label: "Owner/admin email set" },
  { key: "dispatcher_email", label: "Dispatcher email set" },
  { key: "twilio_account_sid", label: "Twilio Account SID set" },
  { key: "twilio_voice_mask_number", label: "Twilio voice mask number set" },
  { key: "resend_domain", label: "Resend email domain set" },
  { key: "resend_from_email", label: "Email from-address set" },
  { key: "base_fare", label: "Base fare set" },
  { key: "per_mile", label: "Per-mile rate set" },
  { key: "stripe_publishable_key", label: "Stripe publishable key set" },
];

export function evaluateReadiness(data = {}) {
  const checks = READINESS_RULES.map((r) => ({
    label: r.label,
    ok: data[r.key] !== undefined && data[r.key] !== null && data[r.key] !== "",
  }));
  const done = checks.filter((c) => c.ok).length;
  return { checks, done, total: checks.length, percent: Math.round((done / checks.length) * 100) };
}

export function generateReadinessSummary(data = {}, secrets = {}) {
  const r = evaluateReadiness(data);
  const secretsProvided = [
    ["Twilio auth token", !!secrets.twilio_auth_token],
    ["Resend API key", !!secrets.resend_api_key],
    ["Stripe secret key", !!secrets.stripe_secret_key],
    ["Stripe webhook secret", !!secrets.stripe_webhook_secret],
  ];
  return [
    `## Deployment readiness — ${v(data.company_name, "Operator")}`,
    "",
    `Config completeness: ${r.done}/${r.total} (${r.percent}%)`,
    "",
    ...r.checks.map((c) => `- [${c.ok ? "x" : " "}] ${c.label}`),
    "",
    "### Secrets (NOT stored — paste into the deployment env at deploy time)",
    ...secretsProvided.map(([label, has]) => `- [${has ? "x" : " "}] ${label} ${has ? "provided in wizard" : "still required"}`),
    "",
    "### Go / No-go",
    r.percent === 100
      ? "- ✅ All blueprint fields captured. Generate the env files, complete the checklists, then deploy."
      : `- ⚠️ ${r.total - r.done} field(s) still missing — complete them before deploying.`,
    "",
  ].join("\n");
}

// ── Combined markdown pack (single downloadable file) ─────────────────────
export function generateProvisioningPack(data = {}, secrets = {}) {
  return [
    `# Provisioning pack — ${v(data.company_name, "Operator")}`,
    `Generated ${new Date().toISOString()}`,
    "",
    "> This is a setup blueprint for a SEPARATE Dispatch OS deployment.",
    "> Secrets are placeholders unless you entered them in the wizard; they were",
    "> never stored on the source platform.",
    "",
    generateReadinessSummary(data, secrets),
    generateDnsChecklist(data),
    generateTwilioWebhookChecklist(data),
    generateResendChecklist(data),
    generateStripeChecklist(data),
    generateFirstAdminChecklist(data, secrets),
    generateGoLiveTestPlan(data),
    "## backend/.env.production",
    "```",
    generateBackendEnv(data, secrets),
    "```",
    "",
    "## frontend/.env",
    "```",
    generateFrontendEnv(data),
    "```",
    "",
  ].join("\n");
}

export function downloadText(filename, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
